import type { JSX } from 'preact';

// Свои иконки вместо эмодзи: одинаково выглядят на всех платформах и красятся через currentColor.
type P = JSX.SVGAttributes<SVGSVGElement>;
const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': 2,
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
} as const;

/** Логотип: «третий глаз» на фиолетово-розовом градиенте; зрачок иногда смотрит по сторонам. */
export function Logo(props: P) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" {...props}>
      <defs>
        <linearGradient id="tp-logo" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#7b5cff" />
          <stop offset="0.55" stop-color="#c15bff" />
          <stop offset="1" stop-color="#ff5fb5" />
        </linearGradient>
      </defs>
      <rect width="48" height="48" rx="14" fill="url(#tp-logo)" />
      <path
        d="M6.5 24C11 16.5 17 13 24 13s13 3.5 17.5 11C37 31.5 31 35 24 35S11 31.5 6.5 24Z"
        fill="#0b0920"
        fill-opacity=".3"
        stroke="#fff"
        stroke-width="2.4"
        stroke-linejoin="round"
      />
      <g class="iris">
        <circle cx="24" cy="24" r="7.4" fill="#fff" />
        <circle cx="24" cy="24" r="3.6" fill="#2a1a7a" />
        <circle cx="26.3" cy="21.7" r="1.4" fill="#fff" />
      </g>
    </svg>
  );
}

export function Bomb(props: P) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <circle cx="10.5" cy="14.5" r="7" fill="currentColor" />
      <path d="M15.5 9.5 18 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" fill="none" />
      <path
        d="M18 7c1-1.6 2.6-1.8 3.4-1.4"
        stroke="#ffb938"
        stroke-width="1.8"
        stroke-linecap="round"
        fill="none"
      />
      <circle class="spark-dot" cx="19" cy="5" r="1.8" fill="#ffd34d" />
      <path
        d="M7 12.2a4 4 0 0 1 2.4-2"
        stroke="#fff"
        stroke-opacity=".45"
        stroke-width="1.6"
        stroke-linecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Ромб-«самоцвет»: своё слово. Форма (а не только цвет) отличает своё слово от обманки. */
export function Gem(props: P) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="M12 2.5 21 10l-9 11.5L3 10l9-7.5Z" fill="currentColor" />
      <path
        d="M3 10h18M12 2.5 8.5 10 12 21.5 15.5 10 12 2.5Z"
        stroke="#fff"
        stroke-opacity=".5"
        stroke-width="1.2"
        fill="none"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export function Help(props: P) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke} {...props}>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M9.4 9.3a2.7 2.7 0 1 1 3.9 2.4c-.9.5-1.3 1-1.3 2" />
      <path d="M12 17.2h.01" stroke-width="2.6" />
    </svg>
  );
}

export function Gear(props: P) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke} {...props}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 14.6a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3h0a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8v0a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
    </svg>
  );
}

export function Chevron({ side, ...props }: P & { side: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke} stroke-width={2.4} {...props}>
      <path d={side === 'left' ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7'} />
    </svg>
  );
}

export function Send(props: P) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke} stroke-width={2.4} {...props}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export function Trophy(props: P) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke} {...props}>
      <path d="M8 4h8v6a4 4 0 0 1-8 0V4ZM8 6H4.5a3 3 0 0 0 3.5 4M16 6h3.5a3 3 0 0 1-3.5 4M12 14v4M8.5 20h7" />
    </svg>
  );
}

export function Bulb(props: P) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke} {...props}>
      <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2V16h5v-.1c0-.8.4-1.5 1-2A6 6 0 0 0 12 3Z" />
    </svg>
  );
}

export function Check(props: P) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="m5 12.5 4.6 4.6L19 7.5" {...stroke} stroke-width="3" />
    </svg>
  );
}

export function List(props: P) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" {...stroke} stroke-width="2.4" />
    </svg>
  );
}
